import React from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import {
  Shield,
  ArrowLeft,
  LayoutDashboard,
  BookOpen,
  Building2,
  DollarSign,
  Users2,
  Activity,
  Gift,
  CreditCard,
  MessageSquare,
  ServerCog,
  LogOut,
  BellRing,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Dedicated Master Admin chrome. Deliberately different from the consumer
 * AppShell: no BottomNav, no consumer footer links, dark analytical top bar,
 * left-sidebar on desktop, tab-strip on mobile. Ordinary users never see
 * this — the /admin/* routes are gated by `AdminProtected` in App.js.
 */
const NAV_ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "adminnav-overview" },
  { to: "/admin/users", label: "Users", icon: Users2, testid: "adminnav-users" },
  { to: "/admin/engagement", label: "Engagement", icon: Activity, testid: "adminnav-engagement" },
  { to: "/admin/proverb", label: "I Need a Proverb", icon: BookOpen, testid: "adminnav-proverb" },
  { to: "/admin/proverb/card-orders", label: "Card Orders", icon: CreditCard, testid: "adminnav-proverb-card-orders" },
  { to: "/admin/sponsors", label: "Sponsors", icon: Building2, testid: "adminnav-sponsors" },
  { to: "/admin/gifts", label: "Gifts", icon: Gift, testid: "adminnav-gifts" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard, testid: "adminnav-subscriptions" },
  { to: "/admin/sms", label: "SMS", icon: MessageSquare, testid: "adminnav-sms" },
  { to: "/admin/trial-reminders", label: "Trial Reminders", icon: BellRing, testid: "adminnav-trial-reminders" },
  { to: "/admin/system-health", label: "System Health", icon: ServerCog, testid: "adminnav-system-health" },
];

export default function AdminShell() {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const handleLogout = () => {
    logout();
    window.location.assign("/admin/login");
  };
  return (
    <div className="min-h-screen bg-[#0F1512] text-[#E7ECE4]" data-testid="admin-shell">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#141B18]/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex w-8 h-8 rounded-xl bg-[#4F6F52]/20 text-[#B7D2AF] items-center justify-center">
              <Shield size={16} />
            </span>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8FA894]">Master Admin</div>
              <div className="font-display text-lg">LifeGuide Ecosystem</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.email && (
              <span
                data-testid="admin-current-email"
                className="hidden sm:inline text-[11px] text-[#8FA894] font-mono px-2"
                title="Currently signed-in admin"
              >
                {user.email}
              </span>
            )}
            <Link
              to="/"
              data-testid="admin-return-to-lifeguide"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#B7D2AF] hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"
            >
              <ArrowLeft size={14} />
              Return to LifeGuide
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#F3D6A6] hover:text-white bg-[#241914] hover:bg-[#2E1F17] border border-[#B4795C]/40 rounded-full px-3 py-1.5"
              title="Sign out of Master Admin"
            >
              <LogOut size={13} />
              Log out
            </button>
          </div>
        </div>
        {/* Mobile tab strip */}
        <nav
          data-testid="admin-mobile-tabs"
          className="lg:hidden overflow-x-auto no-scrollbar border-t border-white/5"
        >
          <ul className="flex gap-1 px-3 py-2 min-w-max">
            {NAV_ITEMS.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.end}
                  data-testid={`m-${it.testid}`}
                  className={({ isActive }) =>
                    `whitespace-nowrap text-xs px-3 py-1.5 rounded-full border ${
                      isActive
                        ? "bg-[#4F6F52] border-[#4F6F52] text-white"
                        : "border-white/10 text-[#B7D2AF] hover:text-white hover:border-white/25"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <div className="max-w-[1400px] mx-auto flex">
        {/* Desktop sidebar */}
        <aside
          data-testid="admin-desktop-sidebar"
          className="hidden lg:block w-56 shrink-0 border-r border-white/10 min-h-[calc(100vh-64px)] py-6 pr-2"
        >
          <ul className="space-y-1 px-2">
            {NAV_ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm ${
                        isActive
                          ? "bg-[#4F6F52]/30 text-white border border-[#4F6F52]/40"
                          : "text-[#B7D2AF] hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <Icon size={15} />
                    {it.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
          <div className="px-4 mt-8 text-[10px] uppercase tracking-[0.18em] text-[#607467]">
            {(() => {
              const host = typeof window !== "undefined" ? window.location.hostname : "";
              // Production hosts for LifeGuide (extend if we add more roots).
              const isProd =
                host === "trylifeguide.com" ||
                host === "www.trylifeguide.com" ||
                host.endsWith(".emergent.host") ||
                host.endsWith(".trylifeguide.com");
              const isPreview =
                host.endsWith(".preview.emergentagent.com") ||
                host === "localhost" ||
                host === "127.0.0.1";
              return isProd
                ? "Production environment"
                : isPreview
                ? "Preview environment"
                : host || "Unknown environment";
            })()}
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6" key={loc.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
